import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateEmail, updateProfile } from 'firebase/auth';
import {
  auth,
  db,
  doc,
  onSnapshot,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  updateDoc,
} from '../firebase';
import {
  defaultNotifyPrefs,
  defaultAppSettings,
  mergeNotifyPrefsFromUser,
  mergeAppSettingsFromUser,
} from '../lib/userPreferences';

function getDuration(from, to) {
  let diff = Math.abs(to - from) / 1000;
  const years = Math.floor(diff / (365.25 * 24 * 3600));
  diff -= years * 365.25 * 24 * 3600;
  const months = Math.floor(diff / (30.44 * 24 * 3600));
  diff -= months * 30.44 * 24 * 3600;
  const days = Math.floor(diff / (24 * 3600));
  diff -= days * 24 * 3600;
  const hours = Math.floor(diff / 3600);
  diff -= hours * 3600;
  const minutes = Math.floor(diff / 60);
  diff -= minutes * 60;
  const seconds = Math.floor(diff);
  return { years, months, days, hours, minutes, seconds };
}

const LANGS = [
  { code: 'en', labelKey: 'lang.en' },
  { code: 'de', labelKey: 'lang.de' },
  { code: 'ar', labelKey: 'lang.ar' },
  { code: 'ku', labelKey: 'lang.ku' },
];

const NOTIFY_CATEGORY_ROWS = [
  { key: 'todoCreated', labelKey: 'notifyCatTodoCreated' },
  { key: 'todoAssigned', labelKey: 'notifyCatTodoAssigned' },
  { key: 'todoReminders', labelKey: 'notifyCatTodoReminders' },
  { key: 'todoDeadlines', labelKey: 'notifyCatTodoDeadlines' },
  { key: 'calendarInvites', labelKey: 'notifyCatCalendarInvites' },
  { key: 'calendarCreated', labelKey: 'notifyCatCalendarCreated' },
  { key: 'calendarReminders', labelKey: 'notifyCatCalendarReminders' },
  { key: 'chatMessages', labelKey: 'notifyCatChat' },
  { key: 'chatUnanswered', labelKey: 'notifyCatChatNudge' },
  { key: 'profileScore', labelKey: 'notifyCatProfileScore' },
];

function ProfileTab({ user, profileScore, refreshUser }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferredLanguage: 'en' });
  const [avatarDraft, setAvatarDraft] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  const [marriage, setMarriage] = useState(null);
  const [duration, setDuration] = useState(null);

  const [notifyPrefs, setNotifyPrefs] = useState(defaultNotifyPrefs);
  const [appSettings, setAppSettings] = useState(defaultAppSettings);

  const uid = user?.uid;
  const showMarriage =
    marriage &&
    Array.isArray(marriage.visibleUserIds) &&
    marriage.visibleUserIds.map(String).includes(String(uid)) &&
    marriage.sinceDate instanceof Date &&
    !Number.isNaN(marriage.sinceDate.getTime());

  useEffect(() => {
    if (!user?.uid) return;
    const pl = user.preferredLanguage;
    const fromProfile =
      pl && LANGS.some((l) => l.code === String(pl).split('-')[0].toLowerCase())
        ? String(pl).split('-')[0].toLowerCase()
        : null;
    const resolvedPl = fromProfile || (i18n.language || 'en').split('-')[0];
    setForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      preferredLanguage: LANGS.some((l) => l.code === resolvedPl) ? resolvedPl : 'en',
    });
    setAvatarDraft('');
    setErr('');
    setOk(false);
  }, [user?.uid, user?.name, user?.email, user?.phone, user?.avatarUrl, user?.preferredLanguage, i18n.language]);

  useEffect(() => {
    if (!user?.uid) return;
    setNotifyPrefs(mergeNotifyPrefsFromUser(user));
    setAppSettings(mergeAppSettingsFromUser(user));
  }, [user]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'settings', 'marriage'), (snap) => {
      if (!snap.exists()) {
        setMarriage(null);
        return;
      }
      const d = snap.data();
      const sinceTs = d.since;
      const sinceDate = sinceTs?.toDate?.() ?? null;
      setMarriage({
        title: d.title || '',
        sinceDate,
        visibleUserIds: d.visibleUserIds || [],
      });
    });
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!showMarriage || !marriage?.sinceDate) {
      setDuration(null);
      return;
    }
    const tick = () => setDuration(getDuration(marriage.sinceDate, new Date()));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [showMarriage, marriage?.sinceDate]);

  const avatarSrc = avatarDraft || user?.avatarUrl;

  const onAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;
    setUploadingAvatar(true);
    setErr('');
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `avatars/${uid}/${Date.now()}_${safe}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      setAvatarDraft(url);
    } catch (e2) {
      setErr(e2.message || t('profileSaveError'));
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!uid || !user) return;
    setSaving(true);
    setErr('');
    setOk(false);
    try {
      const credUser = auth.currentUser;
      if (!credUser) throw new Error('auth');

      const nextName = form.name.trim();
      const nextEmail = form.email.trim();
      const nextPhone = form.phone.trim();
      const nextAvatar = avatarDraft || user.avatarUrl || '';

      if (nextEmail !== (user.email || '').trim()) {
        await updateEmail(credUser, nextEmail);
      }

      await updateProfile(credUser, {
        displayName: nextName || null,
        photoURL: nextAvatar || null,
      });

      await updateDoc(doc(db, 'users', uid), {
        name: nextName,
        email: nextEmail,
        phone: nextPhone,
        avatarUrl: nextAvatar || null,
        preferredLanguage: form.preferredLanguage,
        notifyPrefs,
        appSettings,
      });

      await i18n.changeLanguage(form.preferredLanguage);
      document.documentElement.toggleAttribute('data-haydi-dense', appSettings.denseUi === true);
      document.documentElement.toggleAttribute('data-haydi-reduce-motion', appSettings.reduceMotion === true);
      if (typeof refreshUser === 'function') await refreshUser();
      setOk(true);
      setAvatarDraft('');
    } catch (e2) {
      const code = e2?.code || '';
      if (code === 'auth/requires-recent-login') {
        setErr(t('profileEmailReauth'));
      } else {
        setErr(e2.message || t('profileSaveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const roleLabel = user.role === 'admin' ? t('roleAdmin') : t('roleUser');

  return (
    <div>
      <h1 className="haydi-page-title">{t('profile')}</h1>

      {err && <div className="haydi-alert" role="alert">{err}</div>}
      {ok && <div className="haydi-success" role="status">{t('profileSaved')}</div>}

      <form onSubmit={handleSave} className="haydi-card haydi-card--pad" style={{ marginBottom: '1.25rem' }}>
        <div className="haydi-profile-grid">
          <div className="haydi-avatar-upload">
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="haydi-profile-avatar" />
            ) : (
              <div
                className="haydi-profile-avatar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--haydi-primary-light)',
                  color: 'var(--haydi-primary)',
                  fontSize: '2rem',
                  fontWeight: 800,
                }}
                aria-hidden
              >
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <label className="haydi-btn haydi-btn--secondary haydi-btn--sm">
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onAvatarSelected}
                disabled={uploadingAvatar || saving}
              />
              {uploadingAvatar ? '…' : t('profileUploadPhoto')}
            </label>
          </div>

          <div>
            <div className="haydi-field-grid" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8125rem', fontWeight: 600 }}>
                {t('name')}
                <input
                  className="haydi-input"
                  value={form.name}
                  onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                  autoComplete="name"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8125rem', fontWeight: 600 }}>
                {t('email')}
                <input
                  className="haydi-input"
                  type="email"
                  value={form.email}
                  onChange={(ev) => setForm((f) => ({ ...f, email: ev.target.value }))}
                  autoComplete="email"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8125rem', fontWeight: 600 }}>
                {t('phone')}
                <input
                  className="haydi-input"
                  value={form.phone}
                  onChange={(ev) => setForm((f) => ({ ...f, phone: ev.target.value }))}
                  autoComplete="tel"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8125rem', fontWeight: 600 }}>
                {t('profilePreferredLanguage')}
                <select
                  className="haydi-select haydi-input"
                  value={form.preferredLanguage}
                  onChange={(ev) => setForm((f) => ({ ...f, preferredLanguage: ev.target.value }))}
                >
                  {LANGS.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {t(lang.labelKey)}
                    </option>
                  ))}
                </select>
                <span className="haydi-muted" style={{ fontWeight: 400, fontSize: '0.75rem' }}>
                  {t('profilePreferredLanguageHint')}
                </span>
              </label>
            </div>

            <div
              style={{
                marginTop: '1.5rem',
                paddingTop: '1.25rem',
                borderTop: '1px solid var(--haydi-border)',
              }}
            >
              <h2 className="haydi-section-title" style={{ fontSize: '1.05rem', marginBottom: '0.35rem' }}>
                {t('profileSectionNotifications')}
              </h2>
              <p className="haydi-muted" style={{ fontSize: '0.8125rem', marginBottom: '1rem' }}>
                {t('profileSectionNotificationsHint')}
              </p>
              <div
                className="haydi-field-grid"
                style={{ marginBottom: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
              >
                <label className="haydi-check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={!!notifyPrefs.channels.inApp}
                    onChange={(e) =>
                      setNotifyPrefs((p) => ({
                        ...p,
                        channels: { ...p.channels, inApp: e.target.checked },
                      }))
                    }
                  />
                  {t('notifyChannelInApp')}
                </label>
                <label className="haydi-check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={!!notifyPrefs.channels.push}
                    onChange={(e) =>
                      setNotifyPrefs((p) => ({
                        ...p,
                        channels: { ...p.channels, push: e.target.checked },
                      }))
                    }
                  />
                  {t('notifyChannelPush')}
                </label>
                <label className="haydi-check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={!!notifyPrefs.channels.email}
                    onChange={(e) =>
                      setNotifyPrefs((p) => ({
                        ...p,
                        channels: { ...p.channels, email: e.target.checked },
                      }))
                    }
                  />
                  {t('notifyChannelEmail')}
                </label>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {NOTIFY_CATEGORY_ROWS.map((row) => (
                  <label
                    key={row.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={notifyPrefs.categories[row.key] !== false}
                      onChange={(e) =>
                        setNotifyPrefs((p) => ({
                          ...p,
                          categories: { ...p.categories, [row.key]: e.target.checked },
                        }))
                      }
                    />
                    {t(row.labelKey)}
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                marginTop: '1.5rem',
                paddingTop: '1.25rem',
                borderTop: '1px solid var(--haydi-border)',
              }}
            >
              <h2 className="haydi-section-title" style={{ fontSize: '1.05rem', marginBottom: '0.35rem' }}>
                {t('profileSectionApp')}
              </h2>
              <p className="haydi-muted" style={{ fontSize: '0.8125rem', marginBottom: '1rem' }}>
                {t('profileSectionAppHint')}
              </p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.875rem', marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={appSettings.denseUi === true}
                  onChange={(e) =>
                    setAppSettings((s) => ({ ...s, denseUi: e.target.checked }))
                  }
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{t('appSettingDenseUi')}</strong>
                  <div className="haydi-muted" style={{ fontSize: '0.75rem', fontWeight: 400, marginTop: 2 }}>
                    {t('appSettingDenseUiHint')}
                  </div>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={appSettings.reduceMotion === true}
                  onChange={(e) =>
                    setAppSettings((s) => ({ ...s, reduceMotion: e.target.checked }))
                  }
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{t('appSettingReduceMotion')}</strong>
                  <div className="haydi-muted" style={{ fontSize: '0.75rem', fontWeight: 400, marginTop: 2 }}>
                    {t('appSettingReduceMotionHint')}
                  </div>
                </span>
              </label>
            </div>

            <dl className="haydi-dl" style={{ marginTop: '1rem' }}>
              <div>
                <dt>{t('uuid')}</dt>
                <dd>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8125rem' }}>
                    {user.publicId || '—'}
                  </span>
                  <div className="haydi-muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>{t('profileReadOnlyUuid')}</div>
                </dd>
              </div>
              <div>
                <dt>{t('profileScore')}</dt>
                <dd>{profileScore}</dd>
              </div>
              <div>
                <dt>{t('role')}</dt>
                <dd>{roleLabel}</dd>
              </div>
            </dl>

            <div className="haydi-profile-form-actions">
              <button type="submit" className="haydi-btn haydi-btn--primary" disabled={saving || uploadingAvatar}>
                {saving ? '…' : t('profileSave')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {showMarriage && marriage && duration && (
        <div className="haydi-milestone">
          <h2 className="haydi-section-title" style={{ marginBottom: '0.25rem' }}>
            {marriage.title || t('marriedSince')}
          </h2>
          <p className="haydi-muted" style={{ margin: 0 }}>
            {marriage.sinceDate.toLocaleString()}
          </p>
          <p className="haydi-milestone__timer">
            {t('durationLive', {
              years: duration.years,
              months: duration.months,
              days: duration.days,
              hours: duration.hours,
              minutes: duration.minutes,
              seconds: duration.seconds,
            })}
          </p>
        </div>
      )}
    </div>
  );
}

export default ProfileTab;
